import {
  CARE_SERVICE_ORGANS_PARENT_KEY,
  CareServiceOrgansKey,
  ConditionTemplate,
  RenderedSummary,
  TextToStructuredObservation,
} from '@/api';

import {
  DEGENERATIVE_CHANGE_CONDITION,
  DEGENERATIVE_CHANGE_CONDITIONS,
  ENDOMETRIAL_THICKNESS_CONDITION,
  ENDOMETRIAL_THICKNESS_HIDDEN_VALUES,
  FINDING_CONDITION_TEMPLATE,
  MIN_FINDING_HIGH_SCORE,
  ML_SERVER,
  UTERUS_APPEARANCE_CONDITION,
  UTERUS_APPEARANCE_CONDITION_NAME,
} from '../constant';
import { FindingsByOrgan, ReportLink } from '../model';
import { isEmpty, isNotEmpty } from './is-empty.util';

export const groupFindingsByOrgan = (renderedSummaries: RenderedSummary[]): FindingsByOrgan => {
  const groupedData = {} as FindingsByOrgan;

  renderedSummaries.forEach((renderedSummary) => {
    if (isEmpty(renderedSummary.reportFindings)) {
      return;
    }

    const organ = renderedSummary.reportFindings?.[0]?.template?.organ as
      CareServiceOrgansKey | undefined;

    if (organ) {
      if (!groupedData[organ]) {
        groupedData[organ] = [];
      }

      const conditionTemplate = renderedSummary.reportFindings?.[0].template;

      groupedData[organ].push({
        conditionTemplate: { ...conditionTemplate, organ } as ConditionTemplate,
        renderedSummary: { ...renderedSummary, status: 'draft' },
      });
    }
  });

  return groupedData;
};

export const removeDuplicateConditions = (
  findings: TextToStructuredObservation[],
): TextToStructuredObservation[] => {
  if (findings) {
    const seenConditions = new Set<string>();
    const dedupedFindings: TextToStructuredObservation[] = [];
    const otherFindings: TextToStructuredObservation[] = [];

    findings.forEach((finding) => {
      const { condition } = finding.conditionTemplate;

      if (condition === FINDING_CONDITION_TEMPLATE.OTHER) {
        otherFindings.push(finding);
      } else if (condition && !seenConditions.has(condition)) {
        dedupedFindings.push(finding);
        seenConditions.add(condition);
      } else if (finding.renderedSummary.createdBy !== ML_SERVER) {
        // Replace ML_SERVER-generated findings with user-created findings for the same condition
        const index = dedupedFindings.findIndex((f) => f.conditionTemplate.condition === condition);

        if (index !== -1) {
          dedupedFindings[index] = finding;
        }
      }
    });

    return [...dedupedFindings, ...otherFindings];
  }

  return [];
};

export const categorizeFindings = (
  findings: TextToStructuredObservation[],
): {
  commonFindings: TextToStructuredObservation[];
  defaultFindings: TextToStructuredObservation[];
} => {
  const defaultFindings: TextToStructuredObservation[] = [];
  const commonFindings: TextToStructuredObservation[] = [];

  findings.forEach((finding) => {
    const { condition } = finding.conditionTemplate;

    if (condition?.startsWith('default_question')) {
      defaultFindings.push(finding);
    } else {
      commonFindings.push(finding);
    }
  });

  return { commonFindings, defaultFindings: removeDuplicateConditions(defaultFindings) };
};

export const getFindingsWithHighScores = (data: FindingsByOrgan): FindingsByOrgan => {
  const result = {} as FindingsByOrgan;

  Object.keys(data).forEach((key) => {
    const organData = data[key as CareServiceOrgansKey];

    organData.forEach((item) => {
      const { csdScore, oncoradsScore } = item.renderedSummary;

      if (oncoradsScore !== undefined && csdScore !== undefined) {
        if (oncoradsScore > MIN_FINDING_HIGH_SCORE || csdScore > MIN_FINDING_HIGH_SCORE) {
          result[key as CareServiceOrgansKey] = [
            ...(result[key as CareServiceOrgansKey] ?? []),
            item,
          ];
        }
      }
    });
  });

  return result;
};

export const getConditionTemplates = (
  findings: FindingsByOrgan,
): Record<CareServiceOrgansKey, ConditionTemplate[]> =>
  Object.entries(findings).reduce<Record<CareServiceOrgansKey, ConditionTemplate[]>>(
    (acc, [organ, organFindings]) => {
      if (organ === CARE_SERVICE_ORGANS_PARENT_KEY.SPINE) {
        acc[organ] = organFindings
          .map((finding) => finding.conditionTemplate as ConditionTemplate)
          .filter((finding) => finding.condition !== DEGENERATIVE_CHANGE_CONDITION);
      } else {
        acc[organ as CareServiceOrgansKey] = organFindings.map(
          (finding) => finding.conditionTemplate as ConditionTemplate,
        );
      }

      return acc;
    },
    {} as Record<CareServiceOrgansKey, ConditionTemplate[]>,
  );

export const mergeFindings = (
  links: ReportLink[],
  defaultFindings: FindingsByOrgan,
  findings: FindingsByOrgan,
): FindingsByOrgan =>
  links.reduce((acc, navData) => {
    const organKey = navData.key as CareServiceOrgansKey;
    const organFindings = findings[organKey] || [];
    const defaultOrganFindings = defaultFindings[organKey] || [];

    acc[organKey] = [...defaultOrganFindings, ...organFindings];

    if (navData.sub?.length) {
      navData.sub.forEach((subOrgan) => {
        const subOrganKey = subOrgan.key as CareServiceOrgansKey;
        const subOrganFindings = findings[subOrganKey] || [];
        const defaultSubOrganFindings = defaultFindings[subOrganKey] || [];

        acc[subOrganKey] = [...defaultSubOrganFindings, ...subOrganFindings].map((finding) => ({
          ...finding,
          parentOrgan: organKey,
        }));
      });
    }

    return acc;
  }, {} as FindingsByOrgan);

export const excludeRemovedFindings = (mergedFindings: FindingsByOrgan): FindingsByOrgan =>
  Object.fromEntries(
    Object.entries(mergedFindings).map(([organKey, organFindings]) => [
      organKey,
      organFindings.filter((finding) => finding.renderedSummary?.addendumStatus !== 'removed'),
    ]),
  ) as FindingsByOrgan;

export const checkIfMIFGenerated = (name: string) => {
  const mifGenerated = [ENDOMETRIAL_THICKNESS_CONDITION];

  return mifGenerated.includes(name);
};

export const getDefaultPostMenopausalValue = (status: null | string | undefined) => {
  if (status === 'postMenopausal') {
    return true;
  }

  if (status === 'menstrual' || status === 'menopausal') {
    return false;
  }

  return '';
};

/**
 * Groups degenerative change findings together, sorts them by predefined order,
 * and inserts them back at their original position to maintain context.
 */
export const sortAndGroupFindings = (findings: TextToStructuredObservation[]) => {
  const degenerativeChangeFindings: TextToStructuredObservation[] = [];
  const otherFindings: TextToStructuredObservation[] = [];
  let startIndex = 0;
  let indexUpdated = false;

  findings.forEach((finding, index) => {
    if (DEGENERATIVE_CHANGE_CONDITIONS.includes(finding.conditionTemplate.condition!)) {
      if (!indexUpdated) {
        startIndex = index;
        indexUpdated = true;
      }

      degenerativeChangeFindings.push(finding);
    } else {
      otherFindings.push(finding);
    }
  });

  degenerativeChangeFindings.sort(
    (a, b) =>
      DEGENERATIVE_CHANGE_CONDITIONS.indexOf(a.conditionTemplate.condition!) -
      DEGENERATIVE_CHANGE_CONDITIONS.indexOf(b.conditionTemplate.condition!),
  );

  if (isNotEmpty(degenerativeChangeFindings)) {
    return [
      ...otherFindings.slice(0, startIndex),
      ...degenerativeChangeFindings,
      ...otherFindings.slice(startIndex),
    ];
  }

  return otherFindings;
};

/**
 * Merges default findings and common findings into a single object.
 * If the key is 'spine', the findings are sorted and grouped before merging.
 */
export const mergeDefaultAndCommonFindings = (
  defaultFindings: FindingsByOrgan,
  commonFindings: FindingsByOrgan,
): FindingsByOrgan => {
  const result = { ...defaultFindings };

  Object.entries(commonFindings).forEach(([key, findings]) => {
    let updatedFindings = [...findings];

    if (key === 'spine') {
      updatedFindings = sortAndGroupFindings(updatedFindings);
    }

    if (!result[key as CareServiceOrgansKey]) {
      result[key as CareServiceOrgansKey] = updatedFindings;
    } else {
      result[key as CareServiceOrgansKey] = [
        ...result[key as CareServiceOrgansKey],
        ...updatedFindings,
      ];
    }
  });

  return result;
};

export const createNewRenderedSummaryPayload = (
  observationReportFindingsUuids: string[],
  newReportUuid: string,
  enableAcknowledge: boolean = false,
) => ({
  actionDetails: '',
  actionSummary: '',
  additionalRecommendations: '',
  aiAcknowledged: enableAcknowledge,
  clinicalNotes: '',
  csdScore: 0,
  findingDetails: '',
  findingSummary: '',
  keyImages: [],
  newFinding: true,
  oncoradsScore: 0,
  reportFindings: [],
  reportFindingsUuids: observationReportFindingsUuids,
  reportUuid: newReportUuid,
  status: 'draft' as const,
});

export const isUterusAppearanceAbsentOrPartial = (
  findings: TextToStructuredObservation[],
): boolean => {
  const uterusFinding = findings.find(
    (finding) => finding.conditionTemplate.condition === UTERUS_APPEARANCE_CONDITION,
  );

  if (!uterusFinding) {
    return false;
  }

  const reportFinding = uterusFinding?.renderedSummary?.reportFindings?.[0];
  const conditionDetail = reportFinding?.conditionDetail ?? [];

  return conditionDetail.some(
    (detail) =>
      detail.name === UTERUS_APPEARANCE_CONDITION_NAME &&
      Array.isArray(detail.value) &&
      detail.value.some((value) => ENDOMETRIAL_THICKNESS_HIDDEN_VALUES.includes(value)),
  );
};

export const filterEndometrialThicknessFindings = (
  findings: TextToStructuredObservation[],
): TextToStructuredObservation[] => {
  const shouldHideEndometrialThickness = isUterusAppearanceAbsentOrPartial(findings);

  if (shouldHideEndometrialThickness) {
    return findings.filter(
      (finding) => finding.conditionTemplate.condition !== ENDOMETRIAL_THICKNESS_CONDITION,
    );
  }

  return findings;
};
